import * as Sentry from '@sentry/browser';
import { BehaviorSubject } from 'rxjs';
const systemProfile = {
    id: '00000000-0000-0000-0000-000000000000',
    firstName: 'System',
    lastName: '',
    fullName: 'System',
    email: '',
    active: true,
    username: 'SYSTEM',
    authorities: [],
    notificationPreferences: { textOk: false, emailOk: false },
    metadata: {
        createdAt: new Date('01/01/2019').toISOString(),
        lastUpdatedAt: new Date('01/01/2019').toISOString(),
        createdByUserID: '00000000-0000-0000-0000-000000000000',
        lastUpdatedByUserID: '00000000-0000-0000-0000-000000000000',
    },
};
class ProfileService {
    axiosInstance;
    static auth;
    // Current User
    static currentProfileSubject = new BehaviorSubject(null);
    static currentUserProfile = null;
    // Current Company Roster
    static rosterSubject = new BehaviorSubject([]);
    static rosterLookup = {};
    static roster = [];
    static apps = {
        adminPortal: {
            roster: [],
            idToProfileMap: {},
        },
    };
    static associatedHaulerIdLookup = {};
    static config;
    constructor(auth, axiosInstance, options) {
        this.axiosInstance = axiosInstance;
        ProfileService.auth = auth;
        ProfileService.auth.currentAuthority.subscribe((authority) => {
            if (authority) {
                this.fetchProfile();
                if (options?.settings.defaultApp === 'wayste') {
                    this.wayste.haulerAdmin.query();
                }
            }
            else {
                ProfileService.currentProfileSubject.next(null);
                ProfileService.rosterSubject.next([]);
            }
        });
        if (options) {
            ProfileService.config = options;
        }
    }
    /**
     * Fetches the current user's profile from the server.
     * @returns The current user's profile.
     */
    async fetchProfile() {
        const res = await this.axiosInstance.mainInstance.get('/profile');
        ProfileService.currentUserProfile = res.data;
        Sentry.setUser({ id: res.data.id, email: res.data.email || undefined, username: res.data.username });
        ProfileService.currentProfileSubject.next(res.data);
        return res.data;
    }
    get currentUserProfile() {
        return ProfileService.currentUserProfile;
    }
    get currentUserProfileObservable() {
        return ProfileService.currentProfileSubject.asObservable();
    }
    adminPortal = {
        fetchRoster: async () => {
            const response = await this.axiosInstance.mainInstance.get('/profile/admin-portal/roster');
            ProfileService.apps.adminPortal.roster = response.data || [];
            ProfileService.apps.adminPortal.idToProfileMap = ProfileService.apps.adminPortal.roster.reduce((profileHashmap, profile) => {
                profileHashmap[profile.id] = {
                    ...profile,
                    fullName: profile.fullName || `${profile.firstName} ${profile.lastName}`,
                };
                return profileHashmap;
            }, { [systemProfile.id]: systemProfile });
            ProfileService.roster = ProfileService.apps.adminPortal.roster;
            ProfileService.rosterLookup = ProfileService.apps.adminPortal.idToProfileMap;
            return response.data;
        },
        getRosterFromMemory: () => {
            return ProfileService.apps.adminPortal;
        },
        create: async (input) => {
            const response = await this.axiosInstance.appAdminInstance.post('/profile/admin-portal', input);
            return response.data;
        },
        update: async (profileId, input) => {
            const response = await this.axiosInstance.appAdminInstance.patch(`/profile/admin-portal/${profileId}`, input);
            return response.data;
        },
    };
    wayste = {
        haulerAdmin: {
            query: async () => {
                const { data } = await this.axiosInstance.mainInstance.get('/profile/hauler-admin');
                if (data.length > 0) {
                    data.push(systemProfile);
                }
                ProfileService.roster = data;
                ProfileService.rosterLookup = ProfileService.roster.reduce((profileHashmap, profile) => {
                    profileHashmap[profile.id] = profile;
                    ProfileService.associatedHaulerIdLookup[profile.id] = profile;
                    profile.authorities
                        .filter((authority) => authority.haulerID)
                        .map((authority) => authority.haulerID)
                        // @ts-expect-error - TS is not recognizing that the filter above ensures that haulerID is not null
                        .forEach((y) => (ProfileService.associatedHaulerIdLookup[y] = profile));
                    return profileHashmap;
                }, {});
                ProfileService.rosterSubject.next(ProfileService.roster);
                return data;
            },
            fetch: async (username) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/profile/hauler-admin/${username}`);
                return data;
            },
            update: async (username, payload) => {
                const { data } = await this.axiosInstance.mainInstance.put(`/profile/hauler-admin/${username}`, payload);
                return data;
            },
            create: async (payload) => {
                const { data } = await this.axiosInstance.mainInstance.post('/profile/hauler-admin', payload);
                return data;
            },
            delete: async (username) => {
                const { data } = await this.axiosInstance.mainInstance.delete(`/profile/hauler-admin/${username}`);
                return data;
            },
            setPassword: async (username, payload) => {
                const { data } = await this.axiosInstance.mainInstance.put(`/profile/hauler-admin/${username}/password`, payload);
                return data;
            },
        },
    };
    get rosterObservable() {
        return ProfileService.rosterSubject.asObservable();
    }
    get rosterMap() {
        return ProfileService.rosterLookup;
    }
    get roster() {
        return ProfileService.roster;
    }
    get employees() {
        return ProfileService.roster.filter((profile) => profile.id !== systemProfile.id && profile.active);
    }
    get accountExecutives() {
        return this.employees
            .filter((employee) => employee.authorities.flatMap((a) => a.roles).some((role) => ['contractor-sales', 'commercial-sales'].includes(role)))
            .map((employee) => ({
            id: employee.id,
            firstName: employee.firstName,
            lastName: employee.lastName,
        }));
    }
    get accountManagers() {
        return this.employees
            .filter((employee) => employee.authorities
            .flatMap((a) => a.roles)
            .some((role) => ['contractor-account-managers', 'commercial-account-managers'].includes(role)))
            .map((employee) => ({
            id: employee.id,
            firstName: employee.firstName,
            lastName: employee.lastName,
        }));
    }
    async fetchProfilesByCustomerId(customerId) {
        const res = await this.axiosInstance.mainInstance.get('/profile/admin-portal/' + customerId);
        return res.data.results;
    }
    async adminResetPassword(username, newPassword) {
        await this.axiosInstance.mainInstance.post(ProfileService.config.API.baseApiUrl + `/profile/admin-reset-password/${username}/${newPassword}`, { username, newPassword });
        return;
    }
    async announcementViewed(announcementID) {
        await this.axiosInstance.mainInstance.post(ProfileService.config.API.baseApiUrl + `/profile/announcement/${announcementID}`);
    }
    /**
     * Given an id, return the corresponding roster entry based on local storage.
     * @param {string} id - The id of the roster entry you want to get.
     * @returns The rosterLookup object.
     */
    get(id) {
        return ProfileService.rosterLookup[id];
    }
    getAssociatedHaulerIds(haulerIdOrDriverProfileId) {
        return ProfileService.associatedHaulerIdLookup[haulerIdOrDriverProfileId];
    }
    /**
     * It returns a list of profiles, optionally filtered by role from local storage
     * @param {string} [role] - string
     * @returns An array of profiles.
     */
    list(role) {
        const res = [...ProfileService.roster];
        return role ? res.filter((profile) => profile.authorities.flatMap((a) => a.roles).includes(role)) : res;
    }
    /**
     * Shortcut to return all profiles with the role of driver as it is used frequently.
     *
     * The function is called drivers() because it returns a list of drivers
     * @returns A list of all driver profiles.
     */
    drivers() {
        return this.list('DRIVER');
    }
}
export default ProfileService;
//# sourceMappingURL=Profile.service.js.map